import { useState, useEffect, useRef } from "react";
import Label from "./base/Label.jsx";
import DateRangePicker from "./dateRangePicker.jsx";
import Input from "./base/Input.jsx";
import { theme } from "../theme.js";

/**
 * DateRangeInput
 * Props:
 *  label        – field label
 *  startName    – key for startDate in formData
 *  endName      – key for endDate   in formData
 *  startDate    – value (YYYY-MM-DD string or null)
 *  endDate      – value (YYYY-MM-DD string or null)
 *  setFormData  – state setter (same pattern as Input.jsx)
 *  required     – show asterisk
 *  width        – wrapper width (default "100%")
 */
export default function DateRangeInput({
  label,
  startName,
  endName,
  startDate,
  endDate,
  setFormData,
  onDone,
  required = false,
  width = "100%",
}) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [showPicker, setShowPicker] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close picker when clicking outside
  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  const handleDone = (start, end) => {
    if (onDone) {
      onDone(start, end);
    } else if (setFormData) {
      setFormData((s) => ({ ...s, [startName]: start, [endName]: end }));
    }
    setShowPicker(false);
  };

  const handleNativeChange = (e) => {
    const { name, value } = e.target;
    const newVal = name === startName
      ? { start: value, end: endDate }
      : { start: startDate, end: value };
    if (onDone) {
      onDone(newVal.start, newVal.end);
    } else if (setFormData) {
      setFormData((s) => ({ ...s, [name]: value }));
    }
  };

  const formatDisplay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d) ? "" : d.toLocaleDateString();
  };

  const inputStyle = {
    padding: `${theme.spacing.md} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    fontSize: theme.fonts.size.sm,
    fontFamily: "inherit",
    color: theme.colors.textInverted,
    backgroundColor: "transparent",
    boxSizing: "border-box",
    width: "100%",
    cursor: "pointer",
    outline: "none",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", marginBottom: theme.spacing.lg, width }} ref={wrapperRef}>
      <Label label={label} required={required} />
      <div style={{ height: theme.spacing.xs }} />

      {isMobile ? (
        /* ── Mobile: two native date inputs stacked ── */
        <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.md, width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <span style={labelSmall}>Début</span>
            <Input
              type="date"
              name={startName}
              value={startDate || ""}
              setFormData={setFormData}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <span style={labelSmall}>Fin</span>
            <Input
                type="date"
                name={endName}
                value={endDate || ""}
                setFormData={setFormData}
            />
          </div>
        </div>
      ) : (
        /* ── Desktop: styled trigger → DateRangePicker popup ── */
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            style={{
              ...inputStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: theme.spacing.sm,
              cursor: "pointer",
            }}
          >
            <span style={{ color: startDate ? theme.colors.textInverted : theme.colors.textMuted }}>
              {startDate && endDate
                ? `${formatDisplay(startDate)} → ${formatDisplay(endDate)}`
                : startDate
                ? `${formatDisplay(startDate)} → …`
                : "Sélectionner les dates"}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke={theme.colors.textMuted}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0, transition: "transform 0.2s", transform: showPicker ? "rotate(180deg)" : "rotate(0deg)" }}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </button>

          {showPicker && (
            <div
              style={{
                position: "absolute",
                zIndex: 100,
                top: "calc(100% + 6px)",
                left: 0,
                background: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                borderRadius: theme.radius.md,
              }}
            >
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDone={handleDone}
                onClose={() => setShowPicker(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const labelSmall = {
  fontSize: "11px",
  fontWeight: 500,
  color: theme.colors.textMuted,
  marginBottom: "4px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
